import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext/AuthContext'
import { shareService } from '../../../services/shareService'
import {
  Container,
  Card,
  Title,
  MapInfo,
  MapName,
  MapDescription,
  PermissionsList,
  PermissionItem,
  Button,
  ErrorMessage,
  LoadingMessage,
} from './styled'

export const AcceptShare: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const { user } = useAuth()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [shareInfo, setShareInfo] = useState<{
    mapName?: string
    mapDescription?: string
    permissions: string[]
    expiresAt?: Date
    remainingUses?: number | null
  } | null>(null)

  const loadShareInfo = useCallback(async () => {
    if (!token) {
      setError('Invalid share link')
      setLoading(false)
      return
    }

    try {
      const info = await shareService.getShareLinkInfo(token)
      setShareInfo(info)
    } catch (err) {
      setError('This share link is invalid or has expired')
    } finally {
      setLoading(false)
    }
  }, [token])

  const handleAcceptShare = async () => {
    if (!user) {
      // Redirect to login with return URL
      navigate(`/login?returnUrl=/share/${token}`)
      return
    }

    setAccepting(true)
    setError(null)

    try {
      const sharedAccess = await shareService.acceptShareLink(token!)
      // Redirect to the map
      navigate(`/editor/${sharedAccess.mapId}`)
    } catch (err) {
      console.error(err)
      setError('Failed to accept share')
      setAccepting(false)
    }
  }

  useEffect(() => {
    loadShareInfo()
  }, [loadShareInfo, token])

  if (loading) {
    return (
      <Container>
        <Card>
          <LoadingMessage>Loading share information...</LoadingMessage>
        </Card>
      </Container>
    )
  }

  if (error && !shareInfo) {
    return (
      <Container>
        <Card>
          <ErrorMessage>{error}</ErrorMessage>
          <Button onClick={() => navigate('/')}>Go to Dashboard</Button>
        </Card>
      </Container>
    )
  }

  return (
    <Container>
      <Card>
        <Title>You've been invited to access a map</Title>

        {shareInfo && (
          <MapInfo>
            <MapName>{shareInfo.mapName || 'Untitled Map'}</MapName>
            {shareInfo.mapDescription && (
              <MapDescription>{shareInfo.mapDescription}</MapDescription>
            )}

            <PermissionsList>
              <strong>Permissions:</strong>
              {shareInfo.permissions.map(perm => (
                <PermissionItem key={perm}>{perm}</PermissionItem>
              ))}
            </PermissionsList>

            {shareInfo.expiresAt && (
              <p>
                This link expires on{' '}
                {new Date(shareInfo.expiresAt).toLocaleDateString()}
              </p>
            )}

            {shareInfo.remainingUses !== null && (
              <p>Remaining uses: {shareInfo.remainingUses}</p>
            )}
          </MapInfo>
        )}

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <Button
          onClick={handleAcceptShare}
          disabled={accepting}
          $variant="primary">
          {accepting
            ? 'Accepting...'
            : user
            ? 'Accept Share'
            : 'Sign In to Accept'}
        </Button>

        <Button
          onClick={() => navigate('/')}
          $variant="secondary"
          style={{ marginTop: '0.5rem' }}>
          Cancel
        </Button>
      </Card>
    </Container>
  )
}
